import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuthStore } from "../stores/authStore";
import { ACTION_KEYS } from "../constants/actions";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DaySchedule {
  shiftStart: string;
  shiftEnd: string;
  breakMinutes?: number;
  lunchTime?: string;   // "HH:MM" — start of 30-min unpaid lunch
}

type DailySchedules = Partial<Record<string, DaySchedule>>;

interface Region {
  id: string;
  name: string;
}

interface Team {
  id: string;
  name: string;
}

interface Practice {
  id: string;
  qboDisplayName: string;
  qboCompanyName?: string;
}

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  country?: string;
  languages: string[];
  role?: string;
  jobDescription?: string;
  regionId?: string;
  teamId?: string;
  practicesTrainedOn: string[];
  preferredAccounts: string[];
  payrollProvider?: string;
  employmentType: "full-time" | "part-time" | "contractor";
  startDate?: string;
  isActive: boolean;
  workingDays: number[];
  shiftStart?: string;
  shiftEnd?: string;
  timezone?: string;
  rate?: number;
  rateType?: "hourly" | "salary" | "per-call";
  paymentMethod?: string;
  breakMinutes: number;
  lunchTime?: string;
  includeInCashFlow: boolean;
  flexibleSchedule: boolean;
  dailySchedules?: DailySchedules | null;
}

interface ScheduleOverride {
  id: string;
  teamMemberId: string;
  workDate: string;
  type: string;
  isWorkingDay: boolean;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  notes?: string | null;
}

const NON_WORKING_TYPES = ["leave", "day_off", "holiday"];

const OVERRIDE_TYPE_LABELS: Record<string, string> = {
  leave:       "Leave",
  day_off:     "Day Off",
  holiday:     "Holiday",
  shift_swap:  "Shift Swap",
  extended:    "Extended Hours",
  early_start: "Early Start",
  other:       "Other",
};

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES = [
  { value: "pcc_incoming",        label: "Patient Care Coordinator - Incoming", requiresTeam: true },
  { value: "pcc_outbound",        label: "Patient Care Coordinator - Outbound", requiresTeam: true },
  { value: "onboarding",          label: "Onboarding",                          requiresTeam: false },
  { value: "team_lead",           label: "Team Lead",                           requiresTeam: true },
  { value: "operations_manager",  label: "Operations Manager",                  requiresTeam: false },
  { value: "kpi",                 label: "KPI",                                 requiresTeam: false },
  { value: "upper_management",    label: "Upper Management",                    requiresTeam: false },
  { value: "qa",                  label: "QA",                                  requiresTeam: false },
  { value: "outbound_mod",        label: "Outbound MOD",                        requiresTeam: false },
  { value: "sales_marketing",     label: "Sales/Marketing",                     requiresTeam: false },
  { value: "tech_dev_support",    label: "Tech/Dev Support",                    requiresTeam: false },
  { value: "hr",                  label: "HR",                                  requiresTeam: false },
  { value: "accounting",          label: "Accounting",                          requiresTeam: false },
];

const ROLES_REQUIRING_TEAM = ["pcc_incoming", "pcc_outbound", "team_lead"];
// Operations Manager oversees a whole region, not a specific team — region only, no team picker.
const ROLES_REQUIRING_REGION_ONLY = ["operations_manager"];

const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
  { value: "contractor", label: "Contractor" },
];

const RATE_TYPES = [
  { value: "hourly", label: "Hourly" },
  { value: "salary", label: "Salary" },
  { value: "per-call", label: "Per-call" },
];

const PAYMENT_METHODS = [
  { value: "quickbooks", label: "QuickBooks" },
  { value: "payoneer",   label: "Payoneer" },
  { value: "wise",       label: "Wise" },
  { value: "paypal",     label: "PayPal" },
  { value: "veem",       label: "Veem" },
  { value: "upwork",     label: "Upwork" },
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const COMMON_LANGUAGES = [
  "English", "Spanish", "French", "Portuguese", "German",
  "Italian", "Mandarin", "Arabic", "Hindi", "Japanese",
];

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Manila",
];

// ─── Blank form ───────────────────────────────────────────────────────────────

const blankMember = (): Partial<TeamMember> => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  country: "",
  languages: [],
  role: undefined,
  jobDescription: "",
  regionId: undefined,
  teamId: undefined,
  practicesTrainedOn: [],
  preferredAccounts: [],
  payrollProvider: "",
  employmentType: "full-time",
  startDate: "",
  isActive: true,
  workingDays: [1, 2, 3, 4, 5],
  shiftStart: "09:00",
  shiftEnd: "17:00",
  timezone: "America/Los_Angeles",
  rate: undefined,
  rateType: "hourly",
  paymentMethod: undefined,
  breakMinutes: 0,
  lunchTime: undefined,
  includeInCashFlow: true,
  flexibleSchedule: false,
});

// ─── API helpers ──────────────────────────────────────────────────────────────

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    "Content-Type": "application/json",
  };
}

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`/api/v1${path}`, { ...options, headers: authHeaders() });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Request failed");
  return json.data;
}

function fmt(amount: number) {
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TeamMembers() {
  const canViewPay = useAuthStore((s) => s.hasPermission(ACTION_KEYS.TEAM_MEMBER_PAY));
  const canManageMembers = useAuthStore((s) => s.hasPermission(ACTION_KEYS.TEAM_MEMBER_MANAGE));
  const canAddDeleteMembers = useAuthStore((s) => s.hasPermission(ACTION_KEYS.TEAM_MANAGE));
  const canEditMembers = canManageMembers || canViewPay || canAddDeleteMembers;

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterIncompletePay, setFilterIncompletePay] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [modal, setModal] = useState<{ open: boolean; editing?: TeamMember }>({ open: false });
  const [deleting, setDeleting] = useState<TeamMember | null>(null);
  const [form, setForm] = useState<Partial<TeamMember>>(blankMember());
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [practiceSearch, setPracticeSearch] = useState("");
  const [practiceDropdownOpen, setPracticeDropdownOpen] = useState(false);
  const [scheduleType, setScheduleType] = useState<"global" | "perday">("global");
  const [createLogin, setCreateLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");

  // Schedule overrides (exceptions) state
  const [overrides, setOverrides] = useState<ScheduleOverride[]>([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ workDate: "", type: "leave", scheduledStart: "", scheduledEnd: "", notes: "" });
  const [savingOverride, setSavingOverride] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const membersData = await apiFetch(`/team-members?includeInactive=${showInactive}`);
      setMembers(membersData);
      // These require separate permissions — load best-effort so a 403 doesn't break the page
      apiFetch("/regions").then(setRegions).catch(() => setRegions([]));
      apiFetch("/teams").then(setTeams).catch(() => setTeams([]));
      apiFetch("/customer-profiles?limit=500&isActive=true").then((d) => setPractices(d ?? [])).catch(() => setPractices([]));
    } catch {
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { void load(); }, [load]);

  // Load upcoming overrides whenever we open an existing member for editing
  useEffect(() => {
    if (!modal.editing?.id) { setOverrides([]); return; }
    setOverridesLoading(true);
    const today = new Date().toISOString().slice(0, 10);
    const future = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    fetch(`/api/v1/schedule-overrides/${modal.editing.id}?from=${today}&to=${future}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((r) => r.json())
      .then((j) => setOverrides(j.overrides || []))
      .catch(() => setOverrides([]))
      .finally(() => setOverridesLoading(false));
  }, [modal.editing?.id]);

  const openAdd = () => {
    setForm(blankMember());
    setScheduleType("global");
    setModal({ open: true });
  };

  const openEdit = (m: TeamMember) => {
    // rateType's dropdown visually falls back to "hourly" when unset (see the <select> below),
    // but that fallback only affects what's rendered — it never touches form state. Without this,
    // editing a member who has no rateType yet looks pre-selected to "Hourly" but silently saves
    // nothing for that field unless the dropdown is explicitly re-touched.
    setForm({ ...m, rateType: m.rateType || "hourly" });
    setScheduleType(m.dailySchedules && Object.keys(m.dailySchedules).length > 0 ? "perday" : "global");
    setModal({ open: true, editing: m });
  };

  const closeModal = () => {
    setModal({ open: false });
    setPracticeSearch("");
    setPracticeDropdownOpen(false);
    setScheduleType("global");
    setOverrides([]);
    setOverrideForm({ workDate: "", type: "leave", scheduledStart: "", scheduledEnd: "", notes: "" });
    setCreateLogin(false);
    setLoginEmail("");
  };

  const setField = (key: keyof TeamMember, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleDay = (day: number) => {
    const days = form.workingDays || [];
    setField("workingDays", days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort());
  };

  const toggleLanguage = (lang: string) => {
    const langs = form.languages || [];
    setField("languages", langs.includes(lang) ? langs.filter((l) => l !== lang) : [...langs, lang]);
  };

  const togglePractice = (id: string) => {
    const current = form.practicesTrainedOn || [];
    setField("practicesTrainedOn", current.includes(id) ? current.filter((p) => p !== id) : [...current, id]);
  };

  const togglePreferred = (id: string) => {
    const current = form.preferredAccounts || [];
    setField("preferredAccounts", current.includes(id) ? current.filter((p) => p !== id) : [...current, id]);
  };

  const saveOverride = async () => {
    if (!modal.editing?.id || !overrideForm.workDate) return;
    setSavingOverride(true);
    try {
      const body: Record<string, any> = {
        workDate: overrideForm.workDate,
        type:     overrideForm.type,
        notes:    overrideForm.notes.trim() || null,
      };
      if (!NON_WORKING_TYPES.includes(overrideForm.type)) {
        body.scheduledStart = overrideForm.scheduledStart || null;
        body.scheduledEnd   = overrideForm.scheduledEnd   || null;
      }
      const res = await fetch(`/api/v1/schedule-overrides/${modal.editing.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Failed to save override");
      const saved: ScheduleOverride = json.override;
      setOverrides((prev) => {
        const idx = prev.findIndex((o) => o.workDate === saved.workDate);
        if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
        return [...prev, saved].sort((a, b) => a.workDate.localeCompare(b.workDate));
      });
      setOverrideForm({ workDate: "", type: "leave", scheduledStart: "", scheduledEnd: "", notes: "" });
      toast.success("Exception saved");
    } catch (e: any) {
      toast.error(e.message || "Failed to save exception");
    } finally {
      setSavingOverride(false);
    }
  };

  const deleteOverride = async (workDate: string) => {
    if (!modal.editing?.id) return;
    try {
      const res = await fetch(`/api/v1/schedule-overrides/${modal.editing.id}/${workDate}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      if (!res.ok) throw new Error("Failed to remove exception");
      setOverrides((prev) => prev.filter((o) => o.workDate !== workDate));
      toast.success("Exception removed");
    } catch (e: any) {
      toast.error(e.message || "Failed to remove exception");
    }
  };

  const save = async () => {
    if (!form.firstName?.trim() || !form.lastName?.trim()) {
      toast.error("First and last name are required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        email: form.email || null,
        phone: form.phone || null,
        country: form.country || null,
        role: form.role || null,
        jobDescription: form.jobDescription || null,
        regionId: form.regionId || null,
        teamId: form.teamId || null,
        payrollProvider: form.payrollProvider || null,
        startDate: form.startDate || null,
        shiftStart: form.shiftStart || null,
        shiftEnd: form.shiftEnd || null,
        breakMinutes: form.breakMinutes ?? 0,
        lunchTime: form.lunchTime || null,
        timezone: form.timezone || null,
        dailySchedules: scheduleType === "perday" ? (form.dailySchedules || null) : null,
        rate: form.rate != null && !isNaN(Number(form.rate)) ? Number(form.rate) : null,
        rateType: form.rateType || null,
        paymentMethod: form.paymentMethod || null,
        includeInCashFlow: form.includeInCashFlow ?? true,
        flexibleSchedule: form.flexibleSchedule ?? false,
        practicesTrainedOn: form.practicesTrainedOn || [],
        preferredAccounts: (form.preferredAccounts || []).filter((id) =>
          (form.practicesTrainedOn || []).includes(id)
        ),
      };

      if (modal.editing) {
        const updated = await apiFetch(`/team-members/${modal.editing.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        setMembers((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
        toast.success("Team member updated");
      } else {
        const created = await apiFetch("/team-members", {
          method: "POST",
          body: JSON.stringify(body),
        });
        setMembers((prev) => [...prev, created]);

        if (createLogin && loginEmail.trim()) {
          try {
            const inviteRes = await fetch("/api/v1/team-members/pcc-accounts/invite", {
              method: "POST",
              headers: authHeaders(),
              body: JSON.stringify({ email: loginEmail.trim(), teamMemberId: created.id }),
            });
            const inviteJson = await inviteRes.json();
            if (!inviteRes.ok) throw new Error(inviteJson.message || "Failed to send invite");
            toast.success("Team member added and invite email sent");
          } catch (inviteErr) {
            toast.success("Team member added");
            toast.error(`Login invite failed: ${inviteErr instanceof Error ? inviteErr.message : "Unknown error"}`);
          }
        } else {
          toast.success("Team member added");
        }
      }
      closeModal();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/team-members/${deleting.id}`, { method: "DELETE" });
      setMembers((prev) => prev.filter((m) => m.id !== deleting.id));
      toast.success("Team member removed");
      setDeleting(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete team member");
    } finally {
      setDeleteLoading(false);
    }
  };

  // A member's payroll setup is incomplete until a Plus/pay-access user has filled in
  // all three pay fields — non-pay users can create members but can't set these.
  const isPayrollIncomplete = (m: TeamMember) => !m.rate || !m.rateType || !m.paymentMethod;

  // ── Filters ──
  const filtered = members.filter((m) => {
    const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
    if (search && !fullName.includes(search.toLowerCase()) && !m.email?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterTeam && m.teamId !== filterTeam) return false;
    if (filterRegion && m.regionId !== filterRegion) return false;
    if (filterRole && m.role !== filterRole) return false;
    if (filterType && m.employmentType !== filterType) return false;
    if (canViewPay && filterIncompletePay && !isPayrollIncomplete(m)) return false;
    return true;
  });

  const regionName = (id?: string) => regions.find((r) => r.id === id)?.name ?? "—";
  const teamName = (id?: string) => teams.find((t) => t.id === id)?.name ?? "—";

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    if (!sortKey) return 0;
    let valA: string | number = "";
    let valB: string | number = "";
    switch (sortKey) {
      case "name":
        valA = `${a.firstName} ${a.lastName}`.toLowerCase();
        valB = `${b.firstName} ${b.lastName}`.toLowerCase();
        break;
      case "role":
        valA = (ROLES.find((r) => r.value === a.role)?.label ?? "").toLowerCase();
        valB = (ROLES.find((r) => r.value === b.role)?.label ?? "").toLowerCase();
        break;
      case "team":
        valA = teamName(a.teamId).toLowerCase();
        valB = teamName(b.teamId).toLowerCase();
        break;
      case "region":
        valA = regionName(a.regionId).toLowerCase();
        valB = regionName(b.regionId).toLowerCase();
        break;
      case "type":
        valA = a.employmentType.toLowerCase();
        valB = b.employmentType.toLowerCase();
        break;
      case "rate":
        valA = a.rate ?? -1;
        valB = b.rate ?? -1;
        break;
      case "status":
        valA = a.isActive ? 0 : 1;
        valB = b.isActive ? 0 : 1;
        break;
    }
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ col }: { col: string }) => {
    if (sortKey !== col) return <ChevronsUpDown className="h-3 w-3 ml-1 text-gray-400 inline" />;
    return sortDir === "asc"
      ? <ChevronUp className="h-3 w-3 ml-1 text-blue-500 inline" />
      : <ChevronDown className="h-3 w-3 ml-1 text-blue-500 inline" />;
  };

  const SortTh = ({ col, label }: { col: string; label: string }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => toggleSort(col)}
    >
      {label}<SortIcon col={col} />
    </th>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-blue-600" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Team Members</h1>
              <p className="text-sm text-gray-500">Manage employee profiles and schedules</p>
            </div>
          </div>
          {canEditMembers && (
            <button
              onClick={openAdd}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition"
            >
              <Plus className="h-4 w-4" />
              Add Member
            </button>
          )}
        </div>
      </div>

      <div className="px-6 py-6 space-y-4">
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">All Teams</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">All Regions</option>
            {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">All Roles</option>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">All Types</option>
            {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded" />
            Show inactive
          </label>
          {canViewPay && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={filterIncompletePay} onChange={(e) => setFilterIncompletePay(e.target.checked)} className="rounded" />
              Incomplete payroll setup only
            </label>
          )}
          <span className="text-sm text-gray-400 ml-auto">{filtered.length} member{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-sm text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">No team members found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortTh col="name"   label="Name" />
                    <SortTh col="role"   label="Role" />
                    <SortTh col="team"   label="Team" />
                    <SortTh col="region" label="Region" />
                    <SortTh col="type"   label="Type" />
                    {canViewPay && <SortTh col="rate" label="Rate" />}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Languages</th>
                    <SortTh col="status" label="Status" />
                    {canEditMembers && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sorted.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{m.firstName} {m.lastName}</div>
                        {m.email && <div className="text-xs text-gray-400">{m.email}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{ROLES.find((r) => r.value === m.role)?.label || <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-700">{teamName(m.teamId)}</td>
                      <td className="px-4 py-3 text-gray-700">{regionName(m.regionId)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          m.employmentType === "full-time" ? "bg-blue-100 text-blue-700" :
                          m.employmentType === "part-time" ? "bg-yellow-100 text-yellow-700" :
                          "bg-purple-100 text-purple-700"
                        }`}>
                          {EMPLOYMENT_TYPES.find((t) => t.value === m.employmentType)?.label}
                        </span>
                      </td>
                      {canViewPay && (
                        <td className="px-4 py-3 text-gray-700">
                          {isPayrollIncomplete(m) ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700"
                              title="Missing rate, rate type, or payment method"
                            >
                              Setup incomplete
                            </span>
                          ) : (
                            <span>{fmt(m.rate!)}<span className="text-xs text-gray-400 ml-1">/{m.rateType}</span></span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        {m.languages.length > 0 ? (
                          <span className="text-xs text-gray-600">{m.languages.slice(0, 2).join(", ")}{m.languages.length > 2 ? ` +${m.languages.length - 2}` : ""}</span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {m.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {canEditMembers && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => openEdit(m)} className="p-1.5 text-gray-400 hover:text-blue-600 transition" title="Edit">
                              <Pencil className="h-4 w-4" />
                            </button>
                            {canAddDeleteMembers && (
                              <button onClick={() => setDeleting(m)} className="p-1.5 text-gray-400 hover:text-red-600 transition" title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 my-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {modal.editing ? "Edit Team Member" : "Add Team Member"}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">

              {/* Personal */}
              <Section title="Personal">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name *">
                    <input value={form.firstName || ""} onChange={(e) => setField("firstName", e.target.value)} className={input} />
                  </Field>
                  <Field label="Last Name *">
                    <input value={form.lastName || ""} onChange={(e) => setField("lastName", e.target.value)} className={input} />
                  </Field>
                  <Field label="Email">
                    <input type="email" value={form.email || ""} onChange={(e) => setField("email", e.target.value)} className={input} />
                  </Field>
                  <Field label="Phone">
                    <input value={form.phone || ""} onChange={(e) => setField("phone", e.target.value)} className={input} />
                  </Field>
                  <Field label="Country">
                    <input value={form.country || ""} onChange={(e) => setField("country", e.target.value)} className={input} />
                  </Field>
                </div>
                <Field label="Languages Spoken">
                  <div className="flex flex-wrap gap-2 mt-1">
                    {COMMON_LANGUAGES.map((lang) => {
                      const selected = (form.languages || []).includes(lang);
                      return (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => toggleLanguage(lang)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${selected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"}`}
                        >
                          {lang}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </Section>

              {/* Job */}
              <Section title="Job">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Role">
                    <select
                      value={form.role || ""}
                      onChange={(e) => {
                        const newRole = e.target.value || undefined;
                        setField("role", newRole);
                        const needsRegion = !!newRole && (ROLES_REQUIRING_TEAM.includes(newRole) || ROLES_REQUIRING_REGION_ONLY.includes(newRole));
                        const needsTeam   = !!newRole && ROLES_REQUIRING_TEAM.includes(newRole);
                        // Clear region/team if switching to a role that doesn't need them
                        setForm((prev) => ({
                          ...prev,
                          role: newRole,
                          regionId: needsRegion ? prev.regionId : undefined,
                          teamId:   needsTeam   ? prev.teamId   : undefined,
                        }));
                      }}
                      className={input}
                    >
                      <option value="">— Select Role —</option>
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Employment Type">
                    <select value={form.employmentType || "full-time"} onChange={(e) => setField("employmentType", e.target.value)} className={input}>
                      {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </Field>

                  {/* Region — shown for PCC, Team Lead, and Operations Manager */}
                  {form.role && (ROLES_REQUIRING_TEAM.includes(form.role) || ROLES_REQUIRING_REGION_ONLY.includes(form.role)) && (
                    <Field label="Region *">
                      <select value={form.regionId || ""} onChange={(e) => { setField("regionId", e.target.value || undefined); setField("teamId", undefined); }} className={input}>
                        <option value="">— Select Region —</option>
                        {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </Field>
                  )}
                  {/* Team — only shown for PCC and Team Lead */}
                  {form.role && ROLES_REQUIRING_TEAM.includes(form.role) && (
                    <Field label="Team *">
                      <select value={form.teamId || ""} onChange={(e) => setField("teamId", e.target.value || undefined)} className={input}>
                        <option value="">— Select Team —</option>
                        {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </Field>
                  )}

                  <Field label="Start Date">
                    <input type="date" value={form.startDate || ""} onChange={(e) => setField("startDate", e.target.value)} className={input} />
                  </Field>
                </div>
                <Field label="Job Description">
                  <textarea value={form.jobDescription || ""} onChange={(e) => setField("jobDescription", e.target.value)} rows={3} className={input + " resize-none"} />
                </Field>
                <Field label="Practices Trained On">
                  {/* Selected tags */}
                  {(form.practicesTrainedOn || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(form.practicesTrainedOn || []).map((id) => {
                        const p = practices.find((x) => x.id === id);
                        if (!p) return null;
                        const label = p.qboCompanyName || p.qboDisplayName;
                        return (
                          <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                            {label}
                            <button type="button" onClick={() => togglePractice(id)} className="hover:text-blue-900">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {/* Searchable dropdown */}
                  <div className="relative">
                    <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2 bg-white focus-within:ring-1 focus-within:ring-blue-500">
                      <Search className="h-3.5 w-3.5 text-gray-400 mr-2 shrink-0" />
                      <input
                        type="text"
                        placeholder={practices.length === 0 ? "No practices in portal yet" : "Search practices…"}
                        value={practiceSearch}
                        disabled={practices.length === 0}
                        onChange={(e) => { setPracticeSearch(e.target.value); setPracticeDropdownOpen(true); }}
                        onFocus={() => setPracticeDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setPracticeDropdownOpen(false), 150)}
                        className="flex-1 text-sm text-gray-800 outline-none bg-transparent"
                      />
                      <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    </div>
                    {practiceDropdownOpen && practices.length > 0 && (() => {
                      const selected = new Set(form.practicesTrainedOn || []);
                      const filtered = practices.filter((p) => {
                        const label = (p.qboCompanyName || p.qboDisplayName).toLowerCase();
                        return label.includes(practiceSearch.toLowerCase());
                      });
                      return filtered.length === 0 ? (
                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-2 px-3 text-sm text-gray-400">
                          No matches found
                        </div>
                      ) : (
                        <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filtered.map((p) => {
                            const label = p.qboCompanyName || p.qboDisplayName;
                            const isSelected = selected.has(p.id);
                            return (
                              <li key={p.id}>
                                <button
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => { togglePractice(p.id); setPracticeSearch(""); }}
                                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-gray-50 ${isSelected ? "text-blue-600 font-medium" : "text-gray-700"}`}
                                >
                                  {label}
                                  {isSelected && <Check className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })()}
                  </div>
                </Field>
                {(form.practicesTrainedOn || []).length > 0 && (
                  <Field label="Preferred Accounts">
                    <p className="text-xs text-gray-500 mb-2">Check the accounts this PCC prefers. The schedule engine assigns preferred accounts first.</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                      {(form.practicesTrainedOn || []).map((id) => {
                        const p = practices.find((x) => x.id === id);
                        if (!p) return null;
                        const label = p.qboCompanyName || p.qboDisplayName;
                        const isPreferred = (form.preferredAccounts || []).includes(id);
                        return (
                          <label key={id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isPreferred}
                              onChange={() => togglePreferred(id)}
                              className="rounded accent-indigo-600"
                            />
                            <span className="text-sm text-gray-700">{label}</span>
                            {isPreferred && <span className="ml-auto text-xs text-indigo-600 font-medium">Preferred</span>}
                          </label>
                        );
                      })}
                    </div>
                  </Field>
                )}
                {modal.editing && (
                  <Field label="Status">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.isActive ?? true} onChange={(e) => setField("isActive", e.target.checked)} className="rounded" />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </Field>
                )}
              </Section>

              {/* Schedule */}
              <Section title="Schedule">
                {/* Flexible schedule toggle */}
                <div className="mb-4 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="flexibleSchedule"
                    checked={form.flexibleSchedule ?? false}
                    onChange={(e) => setField("flexibleSchedule", e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <label htmlFor="flexibleSchedule" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Flexible Schedule
                    </label>
                    <p className="text-xs text-gray-400 mt-0.5">
                      No fixed shift hours. This member can clock in and out at any time on any day without approval requests. Time correction adjustments still apply.
                    </p>
                  </div>
                </div>

                {/* Schedule type toggle */}
                <div className={`flex gap-1 mb-4 p-1 bg-gray-100 rounded-lg w-fit ${form.flexibleSchedule ? "opacity-40 pointer-events-none" : ""}`}>
                  <button
                    type="button"
                    onClick={() => setScheduleType("global")}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${scheduleType === "global" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    Global Schedule
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (scheduleType === "global") {
                        const wds = form.workingDays || [];
                        const prefilled: DailySchedules = {};
                        wds.forEach((dow) => {
                          prefilled[String(dow)] = {
                            shiftStart: form.shiftStart || "09:00",
                            shiftEnd:   form.shiftEnd   || "17:00",
                            breakMinutes: form.breakMinutes ?? 0,
                          };
                        });
                        setField("dailySchedules", prefilled);
                      }
                      setScheduleType("perday");
                    }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${scheduleType === "perday" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    Per-Day Schedule
                  </button>
                </div>

                {scheduleType === "global" ? (
                  <>
                    <Field label="Working Days">
                      <div className="flex gap-2 mt-1">
                        {DAY_LABELS.map((label, i) => {
                          const selected = (form.workingDays || []).includes(i);
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => toggleDay(i)}
                              className={`w-9 h-9 rounded-full text-xs font-medium border transition ${selected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-300 hover:border-blue-400"}`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Shift Start">
                        <input type="time" value={form.shiftStart || ""} onChange={(e) => setField("shiftStart", e.target.value)} className={input} />
                      </Field>
                      <Field label="Shift End">
                        <input type="time" value={form.shiftEnd || ""} onChange={(e) => setField("shiftEnd", e.target.value)} className={input} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Break Time (unpaid)">
                        <select value={form.breakMinutes ?? 0} onChange={(e) => setField("breakMinutes", parseInt(e.target.value))} className={input}>
                          <option value={0}>No break</option>
                          <option value={10}>10 minutes</option>
                          <option value={15}>15 minutes</option>
                          <option value={20}>20 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={45}>45 minutes</option>
                          <option value={60}>60 minutes</option>
                          <option value={90}>90 minutes</option>
                        </select>
                      </Field>
                      <Field label="Lunch Time (30 min)">
                        <input
                          type="time"
                          value={form.lunchTime || ""}
                          onChange={(e) => setField("lunchTime", e.target.value || undefined)}
                          className={input}
                          placeholder="HH:MM"
                        />
                      </Field>
                      <Field label="Timezone">
                        <select value={form.timezone || ""} onChange={(e) => setField("timezone", e.target.value)} className={input}>
                          <option value="">— Select —</option>
                          {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                      </Field>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 pr-3 font-medium text-gray-600 w-28">Day</th>
                            <th className="text-center py-2 px-2 font-medium text-gray-600 w-14">Active</th>
                            <th className="text-left py-2 px-2 font-medium text-gray-600">Start</th>
                            <th className="text-left py-2 px-2 font-medium text-gray-600">End</th>
                            <th className="text-left py-2 pl-2 font-medium text-gray-600">Break</th>
                            <th className="text-left py-2 pl-2 font-medium text-gray-600">Lunch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
                            const entry = (form.dailySchedules || {})[String(dow)];
                            const isActive = !!entry;
                            const updateEntry = (patch: Partial<DaySchedule>) => {
                              setField("dailySchedules", {
                                ...(form.dailySchedules || {}),
                                [String(dow)]: { ...(entry || { shiftStart: "09:00", shiftEnd: "17:00" }), ...patch },
                              });
                            };
                            return (
                              <tr key={dow} className={`border-b border-gray-100 ${isActive ? "" : "opacity-50"}`}>
                                <td className="py-2 pr-3 font-medium text-gray-700">
                                  {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dow]}
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => {
                                      const ds = { ...(form.dailySchedules || {}) };
                                      if (e.target.checked) {
                                        ds[String(dow)] = { shiftStart: form.shiftStart || "09:00", shiftEnd: form.shiftEnd || "17:00" };
                                      } else {
                                        delete ds[String(dow)];
                                      }
                                      setField("dailySchedules", ds);
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="time"
                                    disabled={!isActive}
                                    value={entry?.shiftStart || ""}
                                    onChange={(e) => updateEntry({ shiftStart: e.target.value })}
                                    className="border border-gray-300 rounded px-2 py-1 text-sm w-28 disabled:bg-gray-50"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="time"
                                    disabled={!isActive}
                                    value={entry?.shiftEnd || ""}
                                    onChange={(e) => updateEntry({ shiftEnd: e.target.value })}
                                    className="border border-gray-300 rounded px-2 py-1 text-sm w-28 disabled:bg-gray-50"
                                  />
                                </td>
                                <td className="py-2 pl-2">
                                  <select
                                    disabled={!isActive}
                                    value={entry?.breakMinutes ?? ""}
                                    onChange={(e) => updateEntry({ breakMinutes: e.target.value === "" ? undefined : parseInt(e.target.value) })}
                                    className="border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-50"
                                  >
                                    <option value="">Default</option>
                                    <option value={0}>No break</option>
                                    <option value={10}>10 min</option>
                                    <option value={15}>15 min</option>
                                    <option value={20}>20 min</option>
                                    <option value={30}>30 min</option>
                                    <option value={45}>45 min</option>
                                    <option value={60}>60 min</option>
                                    <option value={90}>90 min</option>
                                  </select>
                                </td>
                                <td className="py-2 pl-2">
                                  <input
                                    type="time"
                                    disabled={!isActive}
                                    value={entry?.lunchTime || ""}
                                    onChange={(e) => updateEntry({ lunchTime: e.target.value || undefined })}
                                    className="border border-gray-300 rounded px-2 py-1 text-sm w-28 disabled:bg-gray-50"
                                    title="30-min lunch start time"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Break "Default" falls back to the global break setting.</p>
                    <div className="mt-3">
                      <Field label="Timezone">
                        <select value={form.timezone || ""} onChange={(e) => setField("timezone", e.target.value)} className={input}>
                          <option value="">— Select —</option>
                          {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                      </Field>
                    </div>
                  </>
                )}
              </Section>

              {/* Schedule Exceptions — only available when editing an existing member */}
              {modal.editing && (
                <Section title="Schedule Exceptions">
                  <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                    <p className="text-xs text-gray-500">Override the schedule for a specific date — leave, day off, shift swap, or custom hours.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Date">
                        <input
                          type="date"
                          value={overrideForm.workDate}
                          onChange={(e) => setOverrideForm((f) => ({ ...f, workDate: e.target.value }))}
                          className={input}
                        />
                      </Field>
                      <Field label="Type">
                        <select
                          value={overrideForm.type}
                          onChange={(e) => setOverrideForm((f) => ({ ...f, type: e.target.value }))}
                          className={input}
                        >
                          {Object.entries(OVERRIDE_TYPE_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    {!NON_WORKING_TYPES.includes(overrideForm.type) && (
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Shift Start (optional)">
                          <input
                            type="time"
                            value={overrideForm.scheduledStart}
                            onChange={(e) => setOverrideForm((f) => ({ ...f, scheduledStart: e.target.value }))}
                            className={input}
                          />
                        </Field>
                        <Field label="Shift End (optional)">
                          <input
                            type="time"
                            value={overrideForm.scheduledEnd}
                            onChange={(e) => setOverrideForm((f) => ({ ...f, scheduledEnd: e.target.value }))}
                            className={input}
                          />
                        </Field>
                      </div>
                    )}
                    <Field label="Notes (optional)">
                      <input
                        type="text"
                        placeholder="e.g. HIPAA training, approved by manager"
                        value={overrideForm.notes}
                        onChange={(e) => setOverrideForm((f) => ({ ...f, notes: e.target.value }))}
                        className={input}
                      />
                    </Field>
                    <button
                      type="button"
                      onClick={saveOverride}
                      disabled={savingOverride || !overrideForm.workDate}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition flex items-center gap-2"
                    >
                      {savingOverride && <span className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                      {savingOverride ? "Saving…" : "Save Exception"}
                    </button>
                  </div>

                  {overridesLoading ? (
                    <p className="text-xs text-gray-400">Loading exceptions…</p>
                  ) : overrides.length === 0 ? (
                    <p className="text-xs text-gray-400">No upcoming exceptions in the next 90 days.</p>
                  ) : (
                    <div className="space-y-1.5 mt-1">
                      {overrides.map((o) => (
                        <div key={o.id} className="flex items-start justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                          <div>
                            <span className="text-sm font-medium text-gray-800">{o.workDate}</span>
                            <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              NON_WORKING_TYPES.includes(o.type)
                                ? "bg-red-100 text-red-700"
                                : "bg-blue-100 text-blue-700"
                            }`}>
                              {OVERRIDE_TYPE_LABELS[o.type] ?? o.type}
                            </span>
                            {o.scheduledStart && o.scheduledEnd && (
                              <span className="ml-2 text-xs text-gray-500">{o.scheduledStart}–{o.scheduledEnd}</span>
                            )}
                            {o.notes && <p className="text-xs text-gray-500 mt-0.5">{o.notes}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteOverride(o.workDate)}
                            className="text-gray-400 hover:text-red-500 transition ml-2 mt-0.5 shrink-0"
                            title="Remove exception"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              )}

              {/* Compensation — only shown to users with team-member:pay */}
              {canViewPay && (
                <Section title="Compensation">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Rate">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.rate ?? ""}
                        onChange={(e) => setField("rate", e.target.value === "" ? undefined : parseFloat(e.target.value))}
                        placeholder="0.00"
                        className={input}
                      />
                    </Field>
                    <Field label="Rate Type">
                      <select value={form.rateType || "hourly"} onChange={(e) => setField("rateType", e.target.value)} className={input}>
                        {RATE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Payment Method">
                      <select value={form.paymentMethod || ""} onChange={(e) => setField("paymentMethod", e.target.value || undefined)} className={input}>
                        <option value="">— Select —</option>
                        {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="mt-3 flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="includeInCashFlow"
                      checked={form.includeInCashFlow ?? true}
                      onChange={(e) => setField("includeInCashFlow", e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <label htmlFor="includeInCashFlow" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Include in Cash Flow payroll projections
                      </label>
                      <p className="text-xs text-gray-400 mt-0.5">
                        When enabled, this member's hourly pay will appear as a projected expense in the Cash Flow grid on pay dates.
                      </p>
                    </div>
                  </div>
                </Section>
              )}
            </div>

            {!modal.editing && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <div
                    onClick={() => { setCreateLogin((v) => !v); if (!createLogin) setLoginEmail(""); }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${createLogin ? "bg-blue-600" : "bg-gray-300"}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${createLogin ? "translate-x-4" : "translate-x-1"}`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Also create a login for this team member?</span>
                </label>
                {createLogin && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Login Email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      placeholder="employee@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1.5 text-xs text-gray-400">An invite email will be sent. They'll set their own password via the link.</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={closeModal} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                Cancel
              </button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition flex items-center gap-2">
                {saving && <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {saving ? (modal.editing ? "Saving…" : "Adding…") : (modal.editing ? "Save Changes" : "Add Member")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove Team Member</h3>
            <p className="text-sm text-gray-600 mb-5">
              Are you sure you want to remove <strong>{deleting.firstName} {deleting.lastName}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleting(null)} disabled={deleteLoading} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={confirmDelete} disabled={deleteLoading} className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {deleteLoading && <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {deleteLoading ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

const input = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
